import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Button,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useNotify, useDataProvider } from 'react-admin';
import { DragDropFileUpload } from './DragDropFileUpload';

interface MaintenanceFile {
  id?: number;
  fileId: number;
  file?: {
    id: number;
    name: string;
    url?: string;
    mimeType?: string;
    size?: number;
  };
}

interface MaintenanceFileUploadProps {
  maintenanceId?: number; // For existing maintenance records
  files?: MaintenanceFile[];
  onFilesChange?: (files: MaintenanceFile[]) => void;
  disabled?: boolean;
}


export const MaintenanceFileUpload: React.FC<MaintenanceFileUploadProps> = ({
  maintenanceId,
  files = [],
  onFilesChange,
  disabled = false,
}) => {
  const [localFiles, setLocalFiles] = useState<MaintenanceFile[]>(files);
  const [isLoading, setIsLoading] = useState(false);
  
  const notify = useNotify();
  const dataProvider = useDataProvider();

  useEffect(() => {
    setLocalFiles(files);
  }, [files]);


  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    const mb = bytes / (1024 * 1024);
    return mb < 1 ? `${(bytes / 1024).toFixed(1)} KB` : `${mb.toFixed(1)} MB`;
  };

  const handleFileUploadSuccess = async (uploadedFile: any) => {
    const newFile: MaintenanceFile = {
      fileId: uploadedFile.id,
      file: uploadedFile,
    };

    // If we have a maintenanceId, save to backend immediately
    if (maintenanceId) {
      setIsLoading(true);
      try {
        const result = await dataProvider.create('equipment/files/maintenance/' + maintenanceId, {
          data: {
            fileId: newFile.fileId,
          },
        });
        
        // Add the file with the ID from the backend
        const savedFile = { ...newFile, id: result.data.id };
        const updatedFiles = [...localFiles, savedFile];
        setLocalFiles(updatedFiles);
        onFilesChange?.(updatedFiles);
        
        notify('File attached successfully', { type: 'success' });
      } catch (error) {
        notify('Failed to attach file', { type: 'error' });
        console.error('Error attaching file:', error);
      } finally {
        setIsLoading(false);
      }
    } else {
      // For new maintenance records, just add to the list
      const updatedFiles = [...localFiles, newFile];
      setLocalFiles(updatedFiles);
      onFilesChange?.(updatedFiles);
    }
  };

  const handleRemoveFile = async (index: number) => {
    const fileToRemove = localFiles[index];
    
    // If we have a maintenanceId and the file has an ID, delete from backend
    if (maintenanceId && fileToRemove.id) {
      setIsLoading(true);
      try {
        await dataProvider.delete(
          `equipment/files/maintenance/${maintenanceId}/${fileToRemove.fileId}`,
          { id: fileToRemove.fileId }
        );
        
        const updatedFiles = localFiles.filter((_, i) => i !== index);
        setLocalFiles(updatedFiles);
        onFilesChange?.(updatedFiles);
        
        notify('File removed successfully', { type: 'success' });
      } catch (error) {
        notify('Failed to remove file', { type: 'error' });
        console.error('Error removing file:', error);
      } finally {
        setIsLoading(false);
      }
    } else {
      // For new maintenance records, just remove from the list
      const updatedFiles = localFiles.filter((_, i) => i !== index);
      setLocalFiles(updatedFiles);
      onFilesChange?.(updatedFiles);
    }
  };

  const handleDownloadFile = (file: MaintenanceFile) => {
    if (file.file?.url) {
      window.open(file.file.url, '_blank');
    }
  };

  return (
    <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" color="primary">
          File Attachments
        </Typography>
      </Box>

        {localFiles.length === 0 ? (
          <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
            No files attached. Click "Add File" to upload receipts, photos, or documents.
          </Alert>
        ) : (
          <List>
            {localFiles.map((file, index) => (
              <ListItem
                key={index}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  '&:last-child': { mb: 0 },
                }}
              >
                <ListItemIcon>
                  <AttachFileIcon />
                </ListItemIcon>
                <ListItemText
                  primary={file.file?.name || 'Unnamed file'}
                  secondary={formatFileSize(file.file?.size)}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="download"
                    onClick={() => handleDownloadFile(file)}
                    disabled={!file.file?.url}
                    sx={{ mr: 1 }}
                  >
                    <DownloadIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleRemoveFile(index)}
                    disabled={disabled || isLoading}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}

      <Box sx={{ mt: 2 }}>
        <DragDropFileUpload
          onUploadSuccess={handleFileUploadSuccess}
          folderPath="equipment/maintenance"
          description="Maintenance file"
          acceptTypes="image/*,.pdf,.doc,.docx"
          maxSizeMB={10}
          title="Upload File"
          subtitle="Drag & drop files here or click to select"
          buttonText="Select Files"
          disabled={isLoading || disabled}
        />
      </Box>
    </Paper>
  );
};